import type {
  DeviceCommand,
  DeviceKind,
  DeviceState,
  EquipmentItem,
} from '../types';

/**
 * Devices that act as the tool at the end of the chain — the thing that grips,
 * screws, or picks. A force/torque sensor sits in the chain ahead of the tool
 * and is not one of these; it belongs to {@link FORCE_SENSOR_KINDS}. Callers
 * looking for "the gripper" search this list and will not find a sensor.
 */
const END_EFFECTOR_KINDS: DeviceKind[] = [
  'OnRobot3FG15',
  'OnRobot2FG7',
  'OnRobot2FG14',
  'NoGripper',
  'OnRobotDualQuickChanger',
  'OnRobotScrewdriver',
  'OnRobotVGP20',
  'CustomGripper',
  'SchunkEGx',
  'Robotiq2F',
  'RobotiqEPick',
];

/**
 * Readonly so the union below derives from the list rather than being
 * maintained beside it, and so a caller holding this array — the zero step's
 * `deviceKinds` aliases it — cannot mutate the set every `isForceSensor` call
 * reads from.
 *
 * The Python side cannot import this, so echo-bot's
 * `equipment_configuration_service.py` and `recorder_service.py` and the xacro
 * generator's `FORCE_SENSOR_KINDS` repeat it and must be added to in step. A
 * kind added here and not there records training episodes with no wrench
 * stream and no error, or drops the sensor out of the URDF wrist chain.
 * TypeScript callers import `FORCE_SENSOR_KINDS` or `isForceSensorKind`
 * instead of restating the list.
 */
export const FORCE_SENSOR_KINDS = ['OnRobotHexE', 'RobotiqFT300'] as const;

export type ForceSensorKind = (typeof FORCE_SENSOR_KINDS)[number];

// A kind in both lists would sort into the wrist chain and be searched for as
// the gripper at the same time.
const OVERLAPPING_KINDS = END_EFFECTOR_KINDS.filter((kind) =>
  FORCE_SENSOR_KINDS.includes(kind as ForceSensorKind),
);

if (OVERLAPPING_KINDS.length > 0) {
  throw new Error(
    `Kind is both an end effector and a force sensor: ${OVERLAPPING_KINDS.join(', ')}`,
  );
}

const WELD_MACHINE_KINDS: DeviceKind[] = [
  'WeldMachine',
  'MillerWeldMachine',
  'EsabWeldMachine',
];

/**
 * DH gripper device kinds that are no longer supported.
 * Kept as string literals (not DeviceKind) because these types
 * have been removed from the DeviceConfiguration union.
 */
export const DEPRECATED_DH_KINDS = [
  'DHAG105145',
  'DHPGC30060',
  'DHCGI100170',
] as const;

export const NETWORK_DEVICE: DeviceKind[] = ['ModbusTCPServer'];

/**
 * Takes a plain `string` as well as a `DeviceKind`, because the persisted
 * equipment row types `config.kind` as a string. A string naming no device
 * kind is not an end effector.
 */
export function isEndEffector(value: string | { kind: string }) {
  const kind = typeof value === 'string' ? value : value.kind;

  return (END_EFFECTOR_KINDS as string[]).includes(kind);
}

/**
 * Reads a device's connectivity flag, for the device kinds that report one.
 *
 * Returns `undefined` when the kind has no connectivity concept at all, which
 * callers must keep distinct from `false`: a device that never reports
 * connectivity is not a disconnected device.
 */
export function getGripperConnectivity(
  state: DeviceState | undefined,
): boolean | undefined {
  if (!state || typeof state !== 'object' || !('isConnected' in state)) {
    return undefined;
  }

  return typeof state.isConnected === 'boolean' ? state.isConnected : undefined;
}

export function isForceSensorKind(kind: DeviceKind): kind is ForceSensorKind {
  return FORCE_SENSOR_KINDS.includes(kind as ForceSensorKind);
}

/**
 * Zero/unzero command for a force sensor, in the `${kind}Command` shape
 * `Equipment.executeCommand` matches on.
 *
 * The one place this mapping lives: the REST controller and the Zero step both
 * dispatch through it, so a new sensor kind cannot be recognised by
 * {@link FORCE_SENSOR_KINDS} while still being sent another sensor's command.
 */
export function buildForceSensorCommand(
  kind: ForceSensorKind,
  command: 'zero' | 'unzero',
): DeviceCommand {
  switch (kind) {
    case 'OnRobotHexE':
      return { kind: 'OnRobotHexECommand', command };
    case 'RobotiqFT300':
      return { kind: 'RobotiqFT300Command', command };
    default: {
      const unhandled: never = kind;

      throw new Error(`Unhandled force sensor kind: ${String(unhandled)}`);
    }
  }
}

export function isForceSensor(value: DeviceKind | { kind: DeviceKind }) {
  const kind = typeof value === 'string' ? value : value.kind;

  return isForceSensorKind(kind);
}

/**
 * A tool with a real gripper channel to command or record. Narrower than
 * {@link isEndEffector}: `NoGripper` occupies the tool slot precisely to say
 * nothing is mounted there, so binding a gripper channel to it has nothing to
 * talk to.
 *
 * Use this — not the `getIntegrationType() === 'EndEffector'` test — whenever
 * the question is "which gripper". That integration type is shared by the
 * whole wrist chain (the HEX-E force/torque sensor, mounting brackets and
 * adapters all report it, so they sort into the same equipment-manager
 * section), and picking the first match lands on a sensor on any robot that
 * has one.
 */
export function isGripper(value: DeviceKind | { kind: DeviceKind }) {
  const kind = typeof value === 'string' ? value : value.kind;

  return isEndEffector(kind) && kind !== 'NoGripper';
}

export function hasEndEffector(equipment: EquipmentItem[]): boolean {
  return equipment.some((device) => isEndEffector(device.config.kind));
}

export function isDynamicBase(value: DeviceKind | { kind: DeviceKind }) {
  const kind = typeof value === 'string' ? value : value.kind;

  return kind === 'EwellixLiftTLT';
}

export function isWeldMachine(value: DeviceKind | { kind: DeviceKind }) {
  const kind = typeof value === 'string' ? value : value.kind;

  return WELD_MACHINE_KINDS.includes(kind);
}

export function isMountingBracket(value: DeviceKind | { kind: DeviceKind }) {
  const kind = typeof value === 'string' ? value : value.kind;

  return kind === 'MountingBracket';
}

export function isMountingAdapter(value: DeviceKind | { kind: DeviceKind }) {
  const kind = typeof value === 'string' ? value : value.kind;

  return kind === 'MountingAdapter';
}

/**
 * Wrist chain order, flange outward; everything else follows in registration
 * order. The URDF generator and the visualizer encode the same order, and
 * rotated links do not commute — walk devices in another order and they
 * disagree about where the tool tip is.
 *
 * Order, membership and prose all read from this table so they cannot drift.
 */
const WRIST_CHAIN_ORDER = [
  { matches: isMountingAdapter, name: 'mounting adapter' },
  { matches: isForceSensor, name: 'force/torque sensor' },
  { matches: isMountingBracket, name: 'mounting bracket' },
] as const;

function wristChainRank(value: DeviceKind | { kind: DeviceKind }): number {
  const rank = WRIST_CHAIN_ORDER.findIndex((entry) => entry.matches(value));

  return rank === -1 ? WRIST_CHAIN_ORDER.length : rank;
}

export function compareWristChainOrder(
  a: DeviceKind | { kind: DeviceKind },
  b: DeviceKind | { kind: DeviceKind },
): number {
  return wristChainRank(a) - wristChainRank(b);
}

/**
 * Adapter, force/torque sensor, bracket — the pieces that compose into the
 * wrist chain rather than hanging off it. The URDF generator applies the same
 * partition, and mints a tooltip frame per member so a member's own tool face
 * is a link the planner can rotate about.
 */
export function isWristChainMember(value: DeviceKind | { kind: DeviceKind }) {
  return wristChainRank(value) < WRIST_CHAIN_ORDER.length;
}

/** Anything bolted to the wrist: a tool, or a chain piece ahead of it. */
export function isWristMounted(kind: DeviceKind): boolean {
  return isWristChainMember(kind) || isEndEffector(kind);
}

/**
 * What the shipping flow asks: anything here has to come off before the arm
 * folds into its box. {@link isEndEffector} would clear a sensor-only or
 * adapter-only robot, neither of which is a tool.
 */
export function hasWristMountedEquipment(equipment: EquipmentItem[]): boolean {
  return equipment.some((device) => isWristMounted(device.config.kind));
}

/**
 * The wrist-mounted pieces named in prose, flange outward, for a prompt asking
 * an operator to remove them: "gripper", "force/torque sensor and gripper".
 * Empty when nothing is mounted.
 */
export function describeWristMountedEquipment(
  equipment: EquipmentItem[],
): string {
  const names = [
    ...WRIST_CHAIN_ORDER,
    { matches: isEndEffector, name: 'gripper' },
  ];

  const present = names
    .filter(({ matches }) =>
      equipment.some((device) => matches(device.config.kind)),
    )
    .map(({ name }) => name);

  if (present.length <= 1) {
    return present.join('');
  }

  return `${present.slice(0, -1).join(', ')} and ${present[present.length - 1]}`;
}

/**
 * Device kinematics in wrist-chain composition order. Every
 * `EquipmentInterface` implementor's `getDeviceKinematics` should build its
 * answer here, so the order cannot drift between implementations.
 */
export function wristChainKinematics<Kinematics>(
  devices: Iterable<{ kind: DeviceKind; kinematics: Kinematics }>,
): Kinematics[] {
  return [...devices]
    .sort(compareWristChainOrder)
    .map((device) => device.kinematics);
}

/**
 * Normalize an equipment name for loose comparisons.
 * - lowercases
 * - trims spaces
 * - strips a trailing duplicate suffix like " (2)"
 */
export function normalizeEquipmentName(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s*\(\d+\)\s*$/, '')
    .trim();
}

/**
 * Loosely check if a displayed equipment name refers to a given preset label.
 * Uses normalizeEquipmentName() and checks for startsWith to allow duplicates.
 */
export function isNameLike(displayName: string, presetName: string): boolean {
  const a = normalizeEquipmentName(displayName);
  const b = normalizeEquipmentName(presetName);

  return a.startsWith(b);
}
